import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Agreements.scss';
import './FreeAllocationDetailPage.scss';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Button,
  NotificationContext,
  Modal,
  ModalContext,
  MODAL_ACTIONS,
  ScrollArea,
  Container,
  Layout,
} from '@partner-global-ui/components';
import handleSaveClick from '../../actions/agreementFormHandleSave';
import checkSaveClick from '../../actions/agreementFormBeforeSave';
import FreeAllocationAgreementForm from './FreeAllocationForm/FreeAllocationAgreementForm';
import FreeAllocationChangeHistory from './FreeAllocationForm/FreeAllocationChangeHistory';
import {
  canUpdateExistingAgreement,
} from '../../utils/isValidAgreementForm';
import {
  SUSPENDED, INACTIVE, ACTIVE,
} from '../../constants/agreementState.constants';
import { displayStatuses } from '../../constants/order.constants';
import handleUnsavedChanges, { cleanupHandleUnsavedChanges } from '../../utils/handleUnsavedChanges';
import hasPermission from '../../utils/accessControl/hasPermission';
import roleKeys from '../../utils/accessControl/roleKeys';

const FreeAllocationDetailPage = ({ location, history }) => {
  const { t } = useTranslation();
  const notificationCont = useContext(NotificationContext);
  /* eslint-disable no-restricted-globals */
  const { push } = history;
  /* eslint-enable no-restricted-globals */

  const dispatch = useDispatch();
  // Global State
  const agreementForm = useSelector((state) => state.agreementForm);

  const state = useSelector((reduxState) => reduxState);

  const modalContext = useContext(ModalContext);

  // Destructure from Global State
  const {
    isNewVersion, isSaving,
    agreement, agreement: { agreementVersions },
    hasBeenModified, draftAgreementForm, orderLines,
  } = agreementForm;

  // Local State
  const [warnOrderAffected, setWarnOrderAffected] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [buttonState, setButtonState] = useState({ label: 'Save', disabled: true });

  // Lifecycle Hooks
  useEffect(() => {
    setButtonState({
      ...buttonState,
      label: t('msg_codes_cta_save'),
      name: 'saveAgreementButton',
      actionType: 'save',
      disabled: {
        [true]: () => !canUpdateExistingAgreement(agreementForm),
        [!hasBeenModified.agreement && !hasBeenModified.version]: () => true,
        [isSaving]: () => true,
      }.true(),
    });
  }, [agreementForm, hasBeenModified, draftAgreementForm]);

  // Modal Functions
  const hideWarningModalCancel = () => {
    if (warnOrderAffected) {
      modalContext.dispatch({
        type: MODAL_ACTIONS.HIDE,
      });
      setWarnOrderAffected(false);
      setShowWarningModal(false);
    }
  };

  const hideWarningModalYes = () => {
    if (warnOrderAffected) {
      setWarnOrderAffected(false);
    }
    const shouldSave = true;
    const queryParams = new URLSearchParams(location.search);
    const returnPath = queryParams.has('return') ? queryParams.get('return') : '/agreements';
    handleSaveClick(
      notificationCont,
      agreementForm,
      () => push(returnPath),
      shouldSave,
      hideWarningModalCancel,
    )(dispatch);
  };

  useEffect(() => {
    if (state && state.user) {
      setCanEdit(hasPermission(state, roleKeys.agreement.edit));
    }
  }, [state]);

  useEffect(() => {
    if (agreementForm) {
      handleUnsavedChanges(hasBeenModified.agreement || hasBeenModified.version);
    }
    return () => {
      cleanupHandleUnsavedChanges();
    };
  }, [agreementForm, hasBeenModified]);

  const navigateToPreviousPage = async (
    notificationContLocal,
    shouldSave,
  ) => {
    const {
      initialStatuses: { status: initialStatus },
      agreement: { status: currentStatus },
    } = agreementForm;
    const queryParams = new URLSearchParams(location.search);
    const returnPath = queryParams.has('return') ? queryParams.get('return') : '/agreements';

    const warnAffected = initialStatus === ACTIVE
      && (currentStatus === SUSPENDED || currentStatus === INACTIVE);
    setWarnOrderAffected(warnAffected);

    checkSaveClick(
      notificationContLocal,
      agreementForm,
      () => push(returnPath),
      shouldSave,
      warnOrderAffected,
      setShowWarningModal,
    )(dispatch);
  };

  const orderLineStatusValueMap = displayStatuses.reduce((acc, curr) => ({
    ...acc,
    [curr.value]: curr.translationStringId,
  }), {});

  const scrollAreaHeight = (orderLines.length * 24) + 24 > 400
    ? 400
    : (orderLines.length * 24) + 24;

  const warningModalBody = (
    <div
      name="WarningModalText"
      className="modal-body-content"
    >
      <div
        name="WarningModalIntro"
        className="warning-modal-intro"
        data-testid="warning-modal-intro"
      >
        {t('msg_codes_confirmation_saveChangesAgreement_body')}<br />
      </div>
      <br />
      <div style={{ height: `${scrollAreaHeight}px` }}>
        <ScrollArea overlay>
          <table className="orderlines-table">
            <tbody>
              <tr>
                <th className="tableCell">{t('msg_codes_codes_orderID')}</th>
                <th className="tableCell">{t('msg_codes_codes_orderReference')}</th>
                <th className="tableCell">{t('msg_codes_codes_submissionStatus')}</th>
              </tr>
              {
                orderLines.map((orderLine, index) => {
                  const key = index + 1;
                  return (
                    <tr key={key}>
                      <td className="tableCell">{orderLine.orderNumber}</td>
                      <td className="tableCell">{orderLine.orderReference}</td>
                      <td className="tableCell">{t(orderLineStatusValueMap[orderLine.orderStatus])}</td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );

  const modalProps = {
    id: 'free-allocation-warning-modal',
    name: 'FreeAllocationWarningModal',
    title: t('msg_codes_confirmation_saveChangesAgreement'),
    content: warningModalBody,
    primaryLabel: t('msg_codes_confirmation_saveChangesAgreement_cta_yesSave'),
    secondaryLabel: t('msg_codes_confirmation_leave_cta_no'),
    onPrimary: hideWarningModalYes,
    onSecondary: hideWarningModalCancel,
    onClose: hideWarningModalCancel,
    hideCancelButton: true,
  };

  useEffect(() => {
    if (showWarningModal) {
      modalContext.dispatch({
        type: MODAL_ACTIONS.SHOW,
        payload: <Modal {...modalProps} />,
      });
    }
  }, [showWarningModal]);

  const renderLabel = () => {
    const { canEditVersion, agreementVersionId } = agreementForm;
    const { version } = hasBeenModified;

    const sortedAgreementVersions = _.orderBy(agreementVersions, 'agreementVersionId', ['asc']);
    const foundVersion = sortedAgreementVersions.reduce((foundNumber, localVersion, index) => {
      return localVersion.agreementVersionId === agreementVersionId ? index : foundNumber;
    }, '');

    if (canEditVersion && isNewVersion && version) {
      return _.padStart(sortedAgreementVersions.length + 1, 3, '0');
    }
    return _.padStart(foundVersion + 1, 3, '0');
  };

  const renderButton = () => {
    const name = buttonState.name ? buttonState.name : 'saveAgreementButton';
    const shouldSave = buttonState.actionType === 'save';

    return (
      <Button
        data-testid="save-agreement-button"
        id="saveAgreementButton"
        name={name}
        primary
        onClick={() => navigateToPreviousPage(
          notificationCont,
          shouldSave,
        )}
        disabled={buttonState.disabled}
      >
        {buttonState.label}
      </Button>
    );
  };

  function renderNav() {
    return (
      <div className="free-allocation-agreement-header">
        <div className="title">
          {agreement.agreementId ? (
            <h3 name="agreementHeader">
              {t(
                'msg_codes_agreements_agreementIDnumber',
                { ID: `0000000${agreement.agreementId}`.slice(-8) },
              )}
            </h3>
          ) : (
            <h3 name="agreementHeader">
              Agreement
            </h3>
          )}
          <div className="divider" />
          <span name="dropdown" className="version">
            <h6 name="dropdownLabel">
              {t('msg_codes_agreements_versionNumber', { versionNumber: renderLabel() })}
            </h6>
          </span>
        </div>
        <div className="button">
          {(canEdit) && renderButton()}
        </div>
      </div>
    );
  }

  return (
    <>
      <Prompt
        when={hasBeenModified.agreement || hasBeenModified.version}
        message={t('msg_codes_confirmation_quit_body')}
      />
      {renderNav()}
      <Container>
        <Layout className="agreement free-allocation">
          <FreeAllocationAgreementForm editable={canEdit} />
          <FreeAllocationChangeHistory
            agreementVersionId={agreement.agreementVersions[0].agreementVersionId}
            agreementStatus={agreement.status}
          />
        </Layout>
      </Container>
    </>
  );
};

FreeAllocationDetailPage.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
};

FreeAllocationDetailPage.defaultProps = {
  location: {},
  history: {},
};

export default FreeAllocationDetailPage;
